import { formatDanishCivilDate } from '@/lib/danishDate';
import { classifyPreview, previewKindLabelKey } from '@/utils/previewKind';

const REFERRAL_INBOX_TAG = 'Referral - inbox';
const REFERRAL_TAG_PREFIX = 'Referral - ';

export const DOCUMENT_SOURCE_TAGS = {
  journal: 'journal',
  message: 'message',
  clinic: 'clinic',
} as const;

export type DocumentSourceTag = (typeof DOCUMENT_SOURCE_TAGS)[keyof typeof DOCUMENT_SOURCE_TAGS];

export const DOCUMENT_SOURCE_TAG_LIST = [
  DOCUMENT_SOURCE_TAGS.journal,
  DOCUMENT_SOURCE_TAGS.message,
  DOCUMENT_SOURCE_TAGS.clinic,
] as const;

export const DOCUMENT_SOURCE_LABEL_KEYS = {
  journal: 'documents.source.journal',
  message: 'documents.source.message',
  clinic: 'documents.source.clinic',
} as const;

export const DOCUMENT_SOURCE_LABEL_FALLBACKS = {
  journal: 'Journal',
  message: 'Message',
  clinic: 'Clinic',
} as const;

export function isDocumentSourceTag(value: string): value is DocumentSourceTag {
  return (DOCUMENT_SOURCE_TAG_LIST as readonly string[]).includes(value);
}

/** Canonical source persisted on `documents.tags`. First known tag wins. */
export function documentSourceFromTags(
  tags: readonly string[] | null | undefined,
): DocumentSourceTag | null {
  if (!tags?.length) return null;
  for (const tag of tags) {
    if (isDocumentSourceTag(tag)) return tag;
  }
  return null;
}

/** Journal material is an explicit first-known `journal` tag — never inferred. */
export function isJournalMaterial(tags: readonly string[] | null | undefined): boolean {
  return documentSourceFromTags(tags) === DOCUMENT_SOURCE_TAGS.journal;
}

export function documentSourceLabelKey(source: DocumentSourceTag): string {
  switch (source) {
    case 'journal':
      return DOCUMENT_SOURCE_LABEL_KEYS.journal;
    case 'message':
      return DOCUMENT_SOURCE_LABEL_KEYS.message;
    case 'clinic':
      return DOCUMENT_SOURCE_LABEL_KEYS.clinic;
    default: {
      const _exhaustive: never = source;
      return _exhaustive;
    }
  }
}

export type DocumentSourceTranslate = (key: string, options?: Record<string, unknown>) => string;

export function documentSourceLabel(source: DocumentSourceTag, t: DocumentSourceTranslate): string {
  return t(documentSourceLabelKey(source), {
    defaultValue: DOCUMENT_SOURCE_LABEL_FALLBACKS[source],
  });
}

function referralTagLabel(tag: string, t: DocumentSourceTranslate): string | null {
  if (tag === REFERRAL_INBOX_TAG) return t('documents.source.referral_inbox');
  if (!tag.startsWith(REFERRAL_TAG_PREFIX)) return null;
  const name = tag.slice(REFERRAL_TAG_PREFIX.length).trim();
  if (!name) return t('documents.source.referral_inbox');
  return t('documents.source.referral_tagged', { name });
}

/** Render persisted source tags. Never infer the label from the current page. */
export function formatDocumentSourceLabels(
  tags: readonly string[] | null | undefined,
  t: DocumentSourceTranslate,
): string | null {
  const source = documentSourceFromTags(tags);
  if (source) return documentSourceLabel(source, t);
  if (!tags?.length) return null;
  return tags
    .map((tag) => referralTagLabel(tag, t) ?? tag)
    .join(', ');
}

function listCivilDate(documentDate: string | null | undefined, createdAt: string): string {
  if (documentDate) {
    const formatted = formatDanishCivilDate(documentDate.slice(0, 10));
    if (formatted) return formatted;
  }
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return formatDanishCivilDate(`${parsed.getFullYear()}-${month}-${day}`);
}

function listTypeLabel(mimeType: string, t: DocumentSourceTranslate): string {
  const key = previewKindLabelKey(classifyPreview(mimeType));
  if (key) return t(key);
  return documentTypeLabel(mimeType);
}

export function documentTypeLabel(mimeType: string): string {
  switch (mimeType) {
    case 'application/pdf':
      return 'PDF';
    case 'image/jpeg':
      return 'JPEG';
    case 'image/png':
      return 'PNG';
    case 'image/gif':
      return 'GIF';
    case 'image/webp':
      return 'WEBP';
    case 'text/plain':
      return 'TXT';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'DOCX';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'XLSX';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return 'PPTX';
    default:
      return mimeType.split('/').pop()?.toUpperCase() || 'FILE';
  }
}

export function clinicDocumentStoragePath(input: {
  clinicId: string;
  patientId: string;
  documentSegment: string;
  safeFilename: string;
}): string {
  return `${input.clinicId}/${input.patientId}/${input.documentSegment}/${input.safeFilename}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function documentListMeta(input: {
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  documentDate?: string | null;
  tags: readonly string[] | null | undefined;
  t: DocumentSourceTranslate;
}): string {
  return [
    listTypeLabel(input.mimeType, input.t),
    formatFileSize(input.sizeBytes),
    listCivilDate(input.documentDate, input.createdAt),
    formatDocumentSourceLabels(input.tags, input.t),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function clinicDocumentRegisterArgs(input: {
  clinicId: string;
  patientId: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  categoryId?: string | null;
  documentDate?: string | null;
}): {
  p_clinic_id: string;
  p_patient_id: string;
  p_filename: string;
  p_original_filename: string;
  p_mime_type: string;
  p_size_bytes: number;
  p_storage_path: string;
  p_category_id?: string;
  p_document_date?: string;
} {
  return {
    p_clinic_id: input.clinicId,
    p_patient_id: input.patientId,
    p_filename: input.filename,
    p_original_filename: input.originalFilename,
    p_mime_type: input.mimeType,
    p_size_bytes: input.sizeBytes,
    p_storage_path: input.storagePath,
    ...(input.categoryId ? { p_category_id: input.categoryId } : {}),
    ...(input.documentDate ? { p_document_date: input.documentDate } : {}),
  };
}
